import React, { useState, useEffect, useRef } from "react";
import DataTable from "../../../../Components/UiComponents/DataTable";
import { getCustomerFields } from "../../../../Services/fieldsServices";
import { InputIcon } from "primereact/inputicon";
import { Dialog } from "primereact/dialog";
import { byName, getCustomerControlField } from "../../../../Services/customerServices";
import HeaderSearchBar from "../../../../Components/UiComponents/HeaderSearchBar";
import ButtonElement from "../../../../Components/UiComponents/ButtonElement";
import { updateRef } from "../../../../Services/customerServices";
import "react-toastify/dist/ReactToastify.css";
import { getStages } from "../../../../Services/optionService";
import ToastElement from "../../../../Components/UiComponents/ToastElement";
import CreateNewField from "./CreateNewField";
import EditField from "./EditField";
import Loader from "../../../../Components/UiComponents/Loader";
import { useNavigate } from "react-router-dom";
import ExcelUploader from "./ExcelUploader";
import PlaylistVideos from "../../../../Components/Videos/PlaylistVideos";

export default function Fields(props) {
	const [customerId, setCustomerId] = useState(null);
	const [custmerReferenceData, setCustmerReferenceData] = useState([]);
	const [custmerReferenceFilteredData, setCustmerReferenceFilteredData] = useState([]);
	const [visible, setVisible] = useState(false);
	const [visibleEdit, setVisibleEdit] = useState(false);
	const [position, setPosition] = useState("center");
	const [customerControlFields, setCustomerControlFields] = useState(null);
	const [searchQuery, setSearchQuery] = useState("");
	const [stages, setStages] = useState([]);
	const [updatedReferenceData, setUpdatedReferenceData] = useState([]);
	const [deleteProductDialog, setDeleteProductDialog] = useState(false);
	const [searchedLength, setSearchedLength] = useState(0);
	const [editField, setEditField] = useState([]);
	const [dataToDelete, setDataToDelete] = useState([]);
	const [customerDataAll, setCustomerDataAll] = useState();
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate()
	const [uploadPopup, setUploadPopup] = useState(false);
	const [showVideo, setShowVideo] = useState(false);
	const toastRef = useRef();
	const headers = ["Dropdown Option", "Score", "Active/Inactive"];
	const [dropdownHeaders, setDropdownHeaders] = useState([]);
	useEffect(() => {
		// Fetching end user data from local storage
		const enduserStr = localStorage.getItem("enduser");

		// Checking if end user data exists in local storage
		if (enduserStr) {
			// Parsing end user data
			const enduser = JSON.parse(enduserStr);

			// Extracting customer information from end user data
			const customer = enduser.customer;

			// Setting customer ID state
			setCustomerId(customer);
		} else {
			// Logging if no end user data found in local storage
			navigate("/login")
			localStorage.clear()
		}

		// Fetching stage data
		getStage();
	}, []);

	useEffect(() => {
		// Fetching customer field data
		getCustomerField();

		// Fetching customer control data
		getCustomerControl();
	}, [customerId]);

	// Function to fetch and set stages data
	const getStage = async () => {
		try {
			// Fetch stages data from API
			const stagesResponse = await getStages();

			// Extract and set stages data
			setStages(stagesResponse?.data[0]?.option_choice);
		} catch (error) {
			// Log and handle errors
			toastRef.current.showToast({
				type: 'error',
				message: "Failed to fetch stages data"
			});
		}
	};

	// Function to fetch customer fields data from the server
	const getCustomerField = async () => {
		try {
			if (customerId === null) {
				return;
			}
			setLoading(true);
			// Ensure customerId is not null
			if (customerId !== null) {
				// Fetch customer data from the server
				const customerDataResponse = await getCustomerFields(customerId);
				// Extract reference data from the response
				const referenceData = customerDataResponse?.data?.reference_data;
				setCustomerDataAll(customerDataResponse?.data);
				// Set the reference data in the state
				setCustmerReferenceData(referenceData);
				setCustmerReferenceFilteredData(referenceData);
			}
		} catch (error) {
			toastRef.current.showToast({
				type: 'error',
				message: 'Failed to fetch customer fields'
			});
		} finally {
			setLoading(false)
		}
	};

	// Function to fetch and set customer control fields data
	const getCustomerControl = async () => {
		try {
			if (customerId === null) {
				return;
			}
			// Check if customerId is available
			if (customerId !== null) {
				// Fetch customer control field data
				const customerData = await getCustomerControlField(customerId);

				// Extract and set customer control fields
				const controlFields = customerData?.data?.cust[0].modules[0].tabs[5];
				setCustomerControlFields(controlFields);
				// get dropdown fields
				if (controlFields) {
					setDropdownHeaders(
						controlFields?.tables[0]?.headers?.filter(
							(item) => headers.includes(item.display_name) && item.status
						)
					);
				}
			}
		} catch (error) {
			toastRef.current.showToast({
				type: 'error',
				message: 'Failed to fetch customer fields'
			});
		}
	};

	/**
	 * Function to check and render the display name or option names based on availability in the row data.
	 * @param {Object} rowData - The data of the row being rendered.
	 * @returns {JSX.Element} - The JSX element containing the display name or option names.
	 */
	const checkingDisplayName = (rowData) => {
		// Check if display name exists and is not empty
		if (rowData.display_name != null && rowData.display_name !== "") {
			return <div>{rowData.display_name}</div>;
		} else {
			// If display name doesn't exist or is empty, render option names
			return <div>{rowData.option_names}</div>;
		}
	};

	// This function returns a React component that displays whether the row data has a weightage or not.
	const appliedByTemplate = (rowData) => {
		// Check if weightage exists and is not equal to 0
		const isScored = rowData.weightage && rowData.weightage !== 0;

		// Determine the text to display based on the presence of weightage
		const displayText = isScored ? "Scored" : "Unscored";

		// Return the JSX to display the text
		return (
			<div>
				<div>{displayText}</div>
			</div>
		);
	};

	/**
	 * Template function for rendering approval status icons based on rowData.option_status.
	 * @param {Object} rowData - Data of the current row.
	 * @returns {JSX.Element} - JSX element representing the approval status icon.
	 */
	const approverTemplate = (rowData) => {
		// Determine the icon color based on the option status
		const iconColor = rowData.option_status === "true" ? "green" : "red";

		// Return JSX element with appropriate icon and color
		return (
			<div>
				{/* Render check or cross-circle icon based on option_status */}
				<InputIcon
					className={
						rowData.option_status === "true"
							? "pi pi-check-circle"
							: "pi pi-times-circle"
					}
					style={{ color: iconColor }}
				/>
			</div>
		);
	};

	// Define an array of column configurations for the DataTable
	const columns = [
		// Column configuration for the "Question" field
		{
			field: "Question", // Data field to map from the row data
			header: "Question", // Header label for the column
			body: checkingDisplayName, // Custom renderer for the cell content
			style: { width: "60%" }, // Inline style for the column
			headerClassName: "header-datatable",
			className: 'row-datatable'
		},
		// Column configuration for the "Field" field
		{
			field: "Field", // Data field to map from the row data
			header: "Field", // Header label for the column
			body: appliedByTemplate, // Custom renderer for the cell content
			headerClassName: "header-datatable",
			className: 'row-datatable'
		},
		// Column configuration for the "Referred" field
		{
			field: "Referred", // Data field to map from the row data
			header: "Referred", // Header label for the column
			body: approverTemplate, // Custom renderer for the cell content
			headerClassName: "header-datatable",
			className: 'row-datatable'
		},
	];
	const show = (position) => {
		setPosition(position);
		setVisible(true);
	};

	const handleSearchChange = (e) => {
		setSearchQuery(e.target.value);
		filteredQuestionsArray(e.target.value);
	};
	const handleClearFilter = () => {
		setSearchQuery("");
		filteredQuestionsArray("");
		getCustomerField();
	};


	useEffect(() => {
		setSearchedLength(searchQuery.length)
	}, [searchQuery])

	/**
	 * Filters the customer reference data based on the provided query.
	 * If the query is empty, it retrieves the full customer field data.
	 *
	 * @param {string} query - The search query to filter the customer reference data.
	 */
	const filteredQuestionsArray = async (query) => {
		// Convert the query to lowercase for case-insensitive comparison
		const lowerCaseQuery = query.toLowerCase()
		if (lowerCaseQuery) {
			setSearchedLength(lowerCaseQuery.length);
		}
		// Check if the query is not empty
		if (query.length) {
			// Filter the customer reference data
			// const filteredData = custmerReferenceFilteredData.filter((item) => {
			// 	// Check if any value in the item includes the query string
			// 	return Object.values(item).some((value) =>
			// 		value.toString().toLowerCase().includes(lowerCaseQuery)
			// 	);
			// });

			try {
				if (customerId === null) {
					return;
				}
				if (customerId !== null) {
					const filteredData = await byName(customerId, query);
					// Update the state with the filtered data
					setCustmerReferenceData(filteredData?.data?.reference_data);
				}
			} catch (error) {
				toastRef.current.showToast({
					type: 'error',
					message: 'An error occurred. Please try again or Contact Admin.'
				});
			}
		}
		else {
			getCustomerField();
		}
	};

	const handleHide = () => {
		setVisible(false);
		getCustomerField();
	};

	const handleHideEdit = () => {
		setVisibleEdit(false);
		getCustomerField();
		setSearchQuery('')
	};

	const confirmDeleteProduct = () => {
		setDeleteProductDialog(true);
		filteredQuestionsArray('')
	};

	const deleteField = async () => {
		try {
			if (customerId === null) {
				return;
			}
			setDeleteProductDialog(false);
			setLoading(true);
			const selectedIds = new Set(dataToDelete.map((item) => item._id));
			// Filter the updatedReferenceDatas to exclude items with _id in selectedIds
			const updatedReferenceDatas = custmerReferenceFilteredData.filter((item) => {
				const isExcluded = selectedIds.has(item._id);
				return !isExcluded;
			});


			// Call the API to update the reference data and await the response
			const updatedCusData = {
				...customerDataAll,
				reference_data: updatedReferenceDatas,
			};
			const response = await updateRef(customerId, updatedCusData);

			if (response && response?.status === 200) {
				toastRef.current.showToast({
					type: "warn",
					message: `${customerControlFields?.display_name} Deleted Successfully`,
				});
			} else {
				toastRef.current.showToast({
					type: "error",
					message: `Error: failed to delete ${customerControlFields?.tabs[5].display_name}`,
				});
			}

			getCustomerField();
			setDataToDelete([])
			// Show a success toast message
		} catch (error) {
			toastRef.current.showToast({
				type: 'error',
				message: 'An error occurred. Please try again or Contact Admin.'
			});
		}
		finally {
			setLoading(false);
		}
	};

	const removeField = async (selectedIndex, data) => {
		// Check if the selectedIndex is valid
		if (selectedIndex === -1) {
			return;
		}
		// Create a set of _id values from the selected data
		const selectedIds = new Set(data.map((item) => item._id));
		// Filter the updatedReferenceDatas to exclude items with _id in selectedIds
		const updatedReferenceDatas = custmerReferenceData.filter(
			(item) => !selectedIds.has(item._id)
		);

		setDataToDelete(data);
		// setUpdatedReferenceData(updatedReferenceDatas);
	};

	const showEdit = (position) => {
		setPosition(position);
		setVisibleEdit(true);
	};

	const rowClick = async (data) => {
		if (data && data.length == undefined) {
			setEditField(data);
			showEdit();
			setPosition('right');
		}
		if (data && data.length > 0) {
			setDataToDelete(data)
		} else {
			setDataToDelete([])
		}
	};

	const hideDeleteProductDialog = () => {
		setDeleteProductDialog(false);
	};

	// Define the footer for the delete product dialog with action buttons
	const deleteProductDialogFooter = () => {
		return (
			<div className="cancel-footer">
				<div className='align-center justify-end pb-0'>
					<ButtonElement label="No" className="cancel-new-button" onClick={hideDeleteProductDialog} />
					<ButtonElement type="submit" label="Yes" className="save-button" onClick={deleteField} />
				</div>
			</div>
		);
	}

	const onCloseUploadPopup = () => {
		setUploadPopup(false);
	};

	const handleButtonClick = () => {
        setShowVideo(true);
    };

    const closeVideo = () => {
        setShowVideo(false);
    };


	return (
		<div className="group-table-component">
			<ToastElement ref={toastRef} />
			<Loader loading={loading} />
			<div className="header-design">
				<div>
					<p className="fs-16 fw-600 mar-b-3">{customerControlFields?.display_name}</p>
					<p className="group-table-text-section">
						Use this section to add and edit dropdown options for each question
					</p>
				</div>
				<div>
					<div className="col-md-12 text-right">
						<ButtonElement className="upload-button" type="button" icon="pi pi-video" onClick={handleButtonClick}></ButtonElement>
					</div>
				</div>
				{/* <ButtonElement
					className="upload-button"
					type="button"
					icon="pi pi-question"
				/> */}
			</div>
			<div className="group-table-buttons">
				<div className="delete-button">
					{
						dataToDelete.length > 0 &&
						<ButtonElement
							className="delete-button-icon"
							type="button"
							icon="pi pi-trash"
							onClick={() => confirmDeleteProduct()}
						/>
					}
				</div>
				<div className="group-table-search">
					<ButtonElement
						className="delete-button-icon"
						type="button"
						icon="pi pi-upload"
						onClick={() => setUploadPopup(true)}
					/>
					<HeaderSearchBar
						value={searchQuery}
						onChange={(e) => handleSearchChange(e)}
						onClick={handleClearFilter}
					/>
					<ButtonElement
						className="clear-filter-button"
						type="button"
						icon="pi pi-plus"
						label="New"
						onClick={() => show("right")}
					/>
				</div>
			</div>

			<CreateNewField
				visible={visible}
				position={position}
				customerId={customerId}
				customerControlFields={customerControlFields}
				dropdownHeaders={dropdownHeaders}
				onHide={handleHide}
				customerDataAll={customerDataAll}
			/>
			<EditField
				visible={visibleEdit}
				position={position}
				customerId={customerId}
				customerControlFields={customerControlFields}
				editField={editField}
				customerDataAll={customerDataAll}
				onHide={handleHideEdit}
				getCustomerField={getCustomerField}
				dropdownHeaders={dropdownHeaders}
			/>

			<DataTable
				value={custmerReferenceData}
				columns={columns}
				searchQuery={searchQuery}
				removeField={removeField}
				rowClick={rowClick}
				dataKey="_id"
				isNotReorder={true}
			/>
			<Dialog
				visible={deleteProductDialog}
				breakpoints={{ "960px": "75vw", "641px": "90vw" }}
				header="Confirm"
				modal
				footer={deleteProductDialogFooter}
				onHide={hideDeleteProductDialog}
				className="table-dialog width-32"
				draggable={false}
			>
				<div className="confirmation-content">
					{dataToDelete && (
						<span>
							{dataToDelete.length > 1 ? (
								<span className="table-description">
									Are you sure you want to delete{" "}
									{/* {dataToDelete.map((item, index) => (
										<b key={index}>
											{item.display_name}
											{index < dataToDelete.length - 1 ? ", " : ""}
										</b>
									))} */}
									?
								</span>
							) : (
								<span className="table-description">
									Are you sure you want to delete{" "}
									{/* <b>{dataToDelete[0]?.display_name}</b> */}
									?
								</span>
							)}
						</span>
					)}
				</div>
			</Dialog>
			<Dialog
				header={"Upload Formatted spreadsheet"}
				visible={uploadPopup}
				position="top"
				className="width-50vw"
				onHide={onCloseUploadPopup}
				draggable={false}
			>
				<ExcelUploader onCloseUploadPopup={onCloseUploadPopup}  />
			</Dialog>
			<Dialog id="videoModal" visible={showVideo}
                breakpoints={{
                    '1200px': '80vw',    // Larger modal on bigger screens
                    '1024px': '85vw',    // Slightly smaller for medium screens
                    '769': '90vw',     // Responsive on tablets
                    '641px': '95vw',     // Almost full-width on mobile
                }}
                modal className="table-dialog width-32" draggable={false} width="800px" height="500px" onHide={closeVideo}
            >
                <div className="video-overlay">
                    <div className="video-container">
                        <PlaylistVideos playlistId={process.env.REACT_APP_CONTROL_PANEL_PLAYLIST_ID} apiKey={process.env.REACT_APP_GOOGLE_API_KEY} module="control_panel_fields" />
                    </div>
                </div>
            </Dialog>
		</div>
	);
}
